import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent
} from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../auth/auth.service';
import { AppConfig } from '../../../app.config';
import {catchError, finalize, shareReplay} from 'rxjs/operators';
// tslint:disable-next-line:import-blacklist
import {defer, throwError} from 'rxjs';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService: AuthService;
  private authenticationRecovery$: Observable<string>;
  private dismissedAuthHeader: string;

  constructor(
    private router: Router,
    private injector: Injector,
    private toastr: ToastrService
  ) {
    this.authService = injector.get(AuthService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    const authenticatedRequest = this.transformRequest(req);
    return next.handle(authenticatedRequest).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            if (this.isTokenRequest(req)) {
              // Let the caller decide whether a failed login/refresh request needs interactive authentication.
              return throwError(err);
            } else if (req && req.url.includes('auth') && req.method === 'POST') {
              // if 401 on the login page, it's because they failed to log in - this gets handled in the login component so just rethrow
              return throwError(err);
            } else if (!this.authService.isAuthenticated) {
              return this.logoutAndRedirect(err);
            } else {
              return this.handleUnauthorised(authenticatedRequest, next, err);
            }
          } else if (err.status === 403) {
            return this.logoutAndRedirect(err);
          } else if (this.isApiRequest(req) && err.status >= 400 && err.status < 500 && !this.authService.isAuthenticated) {
            return this.logoutAndRedirect(err);
          } else if (err.status === 500) {
            this.toastr.warning('Internal Server Error - contact a system administrator for assistance.', 'Something went wrong');
            return throwError(err);
          } else {
            return throwError(err);
          }
        }

        return throwError(err);
      }));
  }

  private logoutAndRedirect(err: any)
    : Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    this.authService.logout();
    this.router.navigateByUrl(AppConfig.settings.auth.loginRoute);
    return throwError(err);
  }

  private handleUnauthorised(req: HttpRequest<any>, next: HttpHandler, originalError: any) {
    const originalAuthHeader = req.headers.get('Authorization') || this.authService.getAuthHeader();
    const currentAuthHeader = this.authService.getAuthHeader();

    // A slow response may refer to a token that another request has already renewed.
    if (currentAuthHeader && currentAuthHeader !== originalAuthHeader) {
      return next.handle(this.retryRequestWithToken(req, currentAuthHeader.replace(/^Bearer\s+/i, '')));
    }

    if (!this.authenticationRecovery$) {
      if (this.dismissedAuthHeader === originalAuthHeader) {
        return throwError(originalError);
      }

      // Share the entire recovery, including the prompt, across concurrent failed requests.
      // Keep the route mounted so dirty editor state is preserved.
      this.authenticationRecovery$ = defer(() => this.authService.refreshAccessToken())
        .catch(() => this.waitForAuthHeaderChange(originalAuthHeader))
        .catch(() => this.promptForReauthentication(originalError, originalAuthHeader))
        .pipe(
          finalize(() => this.authenticationRecovery$ = null),
          shareReplay(1)
        );
    }

    return this.authenticationRecovery$
      .mergeMap(token => next.handle(this.retryRequestWithToken(req, token)));
  }

  private promptForReauthentication(originalError: any, originalAuthHeader: string): Observable<string> {
    return Observable.create(observer => {
      let authenticationStarted = false;
      let completed = false;
      let authenticationSubscription;

      const toast = this.toastr.warning(
        'Click here to sign in again and continue saving. Your unsaved changes will be kept.',
        'Your session has expired',
        {
          closeButton: true,
          disableTimeOut: true,
          tapToDismiss: true
        }
      );

      const tapSubscription = toast.onTap.subscribe(() => {
        if (authenticationStarted || completed) {
          return;
        }

        authenticationStarted = true;
        authenticationSubscription = this.authService
          .reauthenticateWithPopup()
          .subscribe(
            token => {
              completed = true;
              this.dismissedAuthHeader = null;
              observer.next(token);
              observer.complete();
            },
            () => {
              completed = true;
              this.dismissedAuthHeader = originalAuthHeader;
              this.toastr.warning(
                'Sign-in was not completed. Your unsaved changes are still on this page; please sign in again.',
                'Sign-in required'
              );
              observer.error(originalError);
            }
          );
      });

      const hiddenSubscription = toast.onHidden.subscribe(() => {
        if (authenticationStarted || completed) {
          return;
        }

        completed = true;
        this.dismissedAuthHeader = originalAuthHeader;
        observer.error(originalError);
      });

      return () => {
        tapSubscription.unsubscribe();
        hiddenSubscription.unsubscribe();
        if (authenticationSubscription) {
          authenticationSubscription.unsubscribe();
        }
        this.toastr.clear(toast.toastId);
      };
    });
  }

  private waitForAuthHeaderChange(originalAuthHeader: string): Observable<string> {
    return Observable.create(observer => {
      let completed = false;
      // Hidden iframe refresh can update localStorage after the MSAL Observable has errored.
      const interval = setInterval(() => {
        const currentAuthHeader = this.authService.getAuthHeader();

        if (!currentAuthHeader || currentAuthHeader === originalAuthHeader) {
          return;
        }

        completed = true;
        clearInterval(interval);
        clearTimeout(timeout);
        observer.next(currentAuthHeader.replace(/^Bearer\s+/i, ''));
        observer.complete();
      }, 250);

      const timeout = setTimeout(() => {
        if (completed) {
          return;
        }

        clearInterval(interval);
        observer.error('Timed out waiting for auth header refresh.');
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    });
  }

  private transformRequest(request: HttpRequest<any>, force: boolean = false) {
    request = this.addAuthHeaders(request, force);

    return request;
  }

  private retryRequestWithToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (!this.isApiRequest(request) || AppConfig.settings.auth.useWindowsAuthentication) {
      return request;
    }

    // Use the token returned by refresh rather than relying on localStorage timing.
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
  }

  private addAuthHeaders(request: HttpRequest<any>, force: boolean = false): HttpRequest<any> {
    // If it's not being sent to our endpoint, don't add the auth headers
    if (!this.isApiRequest(request)) {
      return request;
    }

    if (request.headers.has('Authorization') && !force) {
      return request;
    } else if (AppConfig.settings.auth.useWindowsAuthentication) {
      return request.clone({ withCredentials: true });
    } else {
      const authHeader = this.authService.getAuthHeader();
      return authHeader
        ? request.clone({ setHeaders: { Authorization: authHeader }, withCredentials: true })
        : request.clone({ withCredentials: true });
    }
  }

  private isTokenRequest(req: HttpRequest<any>): boolean {
    if (!req) {
      return false;
    }

    const tokenUrl = `${AppConfig.settings.baseApiUrl}${AppConfig.settings.auth.authTokenUrl}`;
    return req.url.indexOf(tokenUrl) >= 0 || req.url.indexOf(AppConfig.settings.auth.authTokenUrl) >= 0;
  }

  private isApiRequest(req: HttpRequest<any>): boolean {
    return !!req && req.url.indexOf(AppConfig.settings.apiUrl) >= 0;
  }
}
